import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ClaimStatusPanel = ({ selectedCredits, claimType }) => {
  const mockSelectedCredits = [
    {
      id: 'VCS-001-2024',
      projectName: 'Amazon Forest Conservation Project',
      quantity: 1000,
      unitPrice: 12.50,
      registry: 'Verra',
      vintage: 2023,
      ccpStatus: 'approved',
      article6Status: 'compliant',
      correspondingAdjustment: true,
      integrityScore: 95
    },
    {
      id: 'GS-002-2024',
      projectName: 'Kenya Solar Cookstoves Program',
      quantity: 500,
      unitPrice: 15.75,
      registry: 'Gold Standard',
      vintage: 2023,
      ccpStatus: 'approved',
      article6Status: 'compliant',
      correspondingAdjustment: false,
      integrityScore: 92
    }
  ];

  const credits = selectedCredits?.length > 0 ? selectedCredits : mockSelectedCredits;
  const totalQuantity = credits?.reduce((sum, credit) => sum + credit?.quantity, 0);
  const totalValue = credits?.reduce((sum, credit) => sum + (credit?.quantity * credit?.unitPrice), 0);
  const avgIntegrityScore = Math.round(credits?.reduce((sum, credit) => sum + credit?.integrityScore, 0) / credits?.length);

  const complianceChecks = [
    {
      id: 'ccp-approved',
      label: 'CCP Approved Credits',
      status: credits?.every(c => c?.ccpStatus === 'approved') ? 'pass' : 'fail',
      description: 'All selected credits are CCP approved for VCMI claims'
    },
    {
      id: 'article6-compliant',
      label: 'Article 6 Compliance',
      status: credits?.every(c => c?.article6Status === 'compliant') ? 'pass' : 'warning',
      description: 'Credits comply with Article 6 international requirements'
    },
    {
      id: 'double-counting',
      label: 'Double Counting Prevention',
      status: 'pass',
      description: 'No risk of double counting detected across registries'
    },
    {
      id: 'serial-reuse',
      label: 'Serial Number Validation',
      status: 'pass',
      description: 'All serial numbers are unique and not previously retired'
    }
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pass': return { icon: 'CheckCircle', color: 'text-success' };
      case 'warning': return { icon: 'AlertTriangle', color: 'text-warning' };
      case 'fail': return { icon: 'XCircle', color: 'text-error' };
      default: return { icon: 'Circle', color: 'text-muted-foreground' };
    }
  };

  const getClaimTypeInfo = (type) => {
    const types = {
      offsetting: { label: 'Offsetting Claim', icon: 'Target', color: 'bg-primary' },
      contribution: { label: 'Contribution Claim', icon: 'Heart', color: 'bg-accent' },
      inset: { label: 'Inset Claim', icon: 'ArrowDownCircle', color: 'bg-secondary' }
    };
    return types?.[type] || { label: 'Select Claim Type', icon: 'Circle', color: 'bg-muted' };
  };

  const claimInfo = getClaimTypeInfo(claimType);

  return (
    <div className="bg-card border border-border rounded-lg p-4 sticky top-4">
      <div className="mb-4">
        <h3 className="font-semibold text-card-foreground mb-2">Claim Status</h3>
        <div className="flex items-center space-x-2 p-2 bg-muted/50 rounded-lg">
          <div className={`p-1 rounded ${claimInfo?.color}`}>
            <Icon name={claimInfo?.icon} size={16} className="text-white" />
          </div>
          <span className="text-sm font-medium">{claimInfo?.label}</span>
        </div>
      </div>
      {/* Credit Summary */}
      {credits?.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-card-foreground mb-3">Selected Credits</h4>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="text-center p-2 bg-muted/30 rounded">
                <div className="font-bold text-primary">{totalQuantity?.toLocaleString()}</div>
                <div className="text-muted-foreground">tCO2e</div>
              </div>
              <div className="text-center p-2 bg-muted/30 rounded">
                <div className="font-bold text-success">${totalValue?.toLocaleString()}</div>
                <div className="text-muted-foreground">Value</div>
              </div>
            </div>
            
            <div className="text-center p-2 bg-muted/30 rounded">
              <div className="text-xs text-muted-foreground mb-1">Avg Integrity Score</div>
              <div className="flex items-center justify-center space-x-1">
                <div className="font-bold text-success">{avgIntegrityScore}</div>
                <div className="text-xs text-muted-foreground">/100</div>
                <Icon name="TrendingUp" size={12} className="text-success" />
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Compliance Checks */}
      <div className="mb-4">
        <h4 className="text-sm font-medium text-card-foreground mb-3">Compliance Status</h4>
        <div className="space-y-2">
          {complianceChecks?.map((check) => {
            const statusConfig = getStatusIcon(check?.status);
            return (
              <div key={check?.id} className="flex items-start space-x-2 p-2 bg-muted/30 rounded text-xs">
                <Icon name={statusConfig?.icon} size={14} className={`${statusConfig?.color} mt-0.5 flex-shrink-0`} />
                <div className="flex-1">
                  <div className="font-medium text-card-foreground">{check?.label}</div>
                  <div className="text-muted-foreground leading-tight">{check?.description}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* Processing Timeline */}
      <div className="mb-4">
        <h4 className="text-sm font-medium text-card-foreground mb-3">Processing Timeline</h4>
        <div className="space-y-2 text-xs">
          <div className="flex items-center space-x-2">
            <Icon name="Clock" size={12} className="text-muted-foreground" />
            <span className="text-muted-foreground">Estimated: 5-7 business days</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Shield" size={12} className="text-muted-foreground" />
            <span className="text-muted-foreground">Third-party verification available</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="FileText" size={12} className="text-muted-foreground" />
            <span className="text-muted-foreground">Dossier auto-generated</span>
          </div>
        </div>
      </div>
      {/* Quick Actions */}
      <div className="space-y-2">
        <Button variant="outline" size="sm" fullWidth iconName="Save" iconPosition="left">
          Save Draft
        </Button>
        <Button variant="outline" size="sm" fullWidth iconName="Eye" iconPosition="left">
          Preview Claim
        </Button>
      </div>
      {/* Help Section */}
      <div className="mt-4 p-3 bg-muted/30 rounded-lg">
        <div className="flex items-start space-x-2">
          <Icon name="HelpCircle" size={14} className="text-muted-foreground mt-0.5" />
          <div className="text-xs">
            <div className="font-medium text-card-foreground mb-1">Need Help?</div>
            <div className="text-muted-foreground">
              Review VCMI Claims Code guidelines or contact support for assistance.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClaimStatusPanel;